import { useState, useRef, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { logout, getAuth } from "../../utils/auth";
import { userAPI } from "../../services/api";
import packageJson from "../../../package.json";
import {
  FiGrid,
  FiActivity,
  FiCalendar,
  FiAward,
  FiUser,
  FiSettings,
  FiLogOut,
  FiTarget,
  FiChevronLeft,
  FiChevronRight,
  FiMessageSquare,
  FiLock,
} from "react-icons/fi";
import { GiLotus } from "react-icons/gi";
import { useNavigate, useLocation } from "react-router-dom";
import { useCheckIn } from "../../context/CheckInContext";

// ─── RIPPLE EFFECT ────────────────────────────────────────────────────────────
function useRipple() {
  const [ripples, setRipples] = useState([]);
  const trigger = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const id = Date.now();
    setRipples(r => [...r, { id, x, y }]);
    setTimeout(() => setRipples(r => r.filter(rp => rp.id !== id)), 600);
  };
  return { ripples, trigger };
}

// ─── ORBITAL LOGO ─────────────────────────────────────────────────────────────
function OrbitalLogo({ compact = false }) {
  const sizeClass = compact ? "w-[44px] h-[44px]" : "w-[48px] h-[48px]";
  return (
    <div className={`relative ${sizeClass} flex-shrink-0`}>
      {/* Orbit ring */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
        className="absolute inset-0"
      >
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[5px] h-[5px] rounded-full bg-white/70"
          style={{ top: 2, left: "50%" }}
        />
      </motion.div>
      <motion.div
        animate={{ rotate: -360 }}
        transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
        className="absolute inset-[6px]"
      >
        <div
          className="absolute w-[4px] h-[4px] rounded-full bg-white/40"
          style={{ top: 0, left: "50%", transform: "translateX(-50%) translateY(-50%)" }}
        />
      </motion.div>

      {/* Core */}
      <motion.div
        animate={{ boxShadow: ["0 0 0 0 rgba(242,154,12,0)", "0 0 0 6px rgba(242,154,12,0.18)", "0 0 0 0 rgba(242,154,12,0)"] }}
        transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute inset-0 rounded-[16px] flex items-center justify-center overflow-hidden border border-white/20 shadow-lg bg-white"
      >
        <img src="/assets/logo.jpeg" alt="Divyyog Foundation Logo" className="w-full h-full object-cover rounded-[16px]" />
      </motion.div>
    </div>
  );
}
// ── Avatar initials ────────────────────────────────────────────────────────
  const getInitials = (user) => {
    if (!user) return "U";
    const name = user.name || user.fullName || user.userName || user.email || "";
    return name.split(" ").slice(0,2).map(w => w[0]).join("").toUpperCase() || "U";
  };
// ─── NAV ITEM ─────────────────────────────────────────────────────────────────
function NavItem({ item, expanded, isActive, onClick }) {
  const { ripples, trigger } = useRipple();
  const isOlive = item.oliveActive;

  const activeGrad = isOlive
    ? "linear-gradient(135deg,#6B8A5A,#8BA47A)"
    : "linear-gradient(135deg,#F29A0C,#E97818)";
  const activeShadow = isOlive
    ? "0 10px 28px rgba(107,138,90,0.38)"
    : "0 10px 28px rgba(242,154,12,0.38)";

  const Icon = item.icon;

  const handleClick = (e) => {
    trigger(e);
    onClick();
  };

  return (
    <motion.div
      onClick={handleClick}
      whileHover={{ x: isActive ? 0 : 5 }}
      transition={{ type: "spring", stiffness: 400, damping: 28 }}
      className="relative h-[52px] flex items-center rounded-[18px] cursor-pointer overflow-hidden select-none"
      style={isActive
        ? { background: activeGrad, boxShadow: activeShadow }
        : {}}
    >
      {/* Hover bg */}
      {!isActive && (
        <motion.div
          className="absolute inset-0 rounded-[18px]"
          initial={{ opacity: 0 }}
          whileHover={{ opacity: 1 }}
          style={{ background: "rgba(242,154,12,0.06)" }}
        />
      )}

      {/* Left indicator bar */}
      {!isActive && (
        <motion.div
          className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-r-[3px]"
          initial={{ height: 0 }}
          whileHover={{ height: "55%" }}
          transition={{ type: "spring", stiffness: 400, damping: 28 }}
          style={{ background: "linear-gradient(180deg,#F29A0C,#E97818)", transformOrigin: "center" }}
        />
      )}

      {/* Ripples */}
      {ripples.map(rp => (
        <motion.span
          key={rp.id}
          className="absolute rounded-full pointer-events-none"
          style={{ left: rp.x - 30, top: rp.y - 30, width: 60, height: 60, background: "rgba(255,255,255,0.25)" }}
          initial={{ scale: 0, opacity: 0.7 }}
          animate={{ scale: 3.5, opacity: 0 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
        />
      ))}

      {/* Icon */}
      <div className="w-[52px] h-[52px] flex items-center justify-center flex-shrink-0">
        <motion.span
          whileHover={!isActive ? { scale: 1.18, rotate: -6 } : {}}
          transition={{ type: "spring", stiffness: 400, damping: 20 }}
          className="text-[20px]"
          style={{ color: isActive ? "#fff" : "#888" }}
        >
          <Icon />
        </motion.span>
      </div>

      {/* Label */}
      <AnimatePresence>
        {expanded && (
          <motion.span
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.22, ease: [0.25, 1, 0.5, 1] }}
            className="text-[13.5px] font-semibold whitespace-nowrap flex-1 min-w-0"
            style={{ color: isActive ? "#fff" : "#555", fontFamily: "'Inter', sans-serif", letterSpacing: "-0.2px" }}
          >
            {item.label}
          </motion.span>
        )}
      </AnimatePresence>

      {/* Badge */}
      {item.badge && !item.isLocked && (
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.6 }}
              transition={{ duration: 0.2, delay: 0.08 }}
              className="mr-3 w-[22px] h-[22px] rounded-[8px] flex items-center justify-center text-[10px] font-black flex-shrink-0"
              style={isActive
                ? { background: "rgba(255,255,255,0.25)", color: "#fff" }
                : { background: "rgba(242,154,12,0.12)", color: "#C8660A" }}
            >
              {item.badge}
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {/* Lock Badge */}
      {item.isLocked && (
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.6 }}
              className="mr-3 w-[22px] h-[22px] rounded-[8px] flex items-center justify-center text-[10px] bg-rose-500/15 text-rose-600 font-black shrink-0"
              title="Feature locked on current tier"
            >
              <FiLock size={12} />
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {/* Tooltip (collapsed only) */}
      {!expanded && (
        <motion.div
          className="absolute left-[calc(100%+12px)] z-50 pointer-events-none"
          initial={{ opacity: 0, x: -6 }}
          whileHover={{ opacity: 0 }}
          style={{ opacity: 0 }}
        >
          <div className="bg-[#1F1F1F] text-white text-[12px] font-semibold px-3 py-1.5 rounded-[10px] whitespace-nowrap shadow-lg relative">
            {item.label}
            <div className="absolute top-1/2 left-0 -translate-y-1/2 -translate-x-full border-[5px] border-transparent border-r-[#1F1F1F]" />
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
// ─── SIDEBAR COMPONENT ────────────────────────────────────────────────────────
export default function Sidebar({ expanded, setExpanded }) {
  const [pinned, setPinned] = useState(false);
  const hoverTimeout = useRef(null);
  const navigate = useNavigate ();
  const location = useLocation();

  const { stats, unreadNotifCount, videosCount, liveSessionsCount } = useCheckIn();

  const [auth, setAuthState] = useState(getAuth);
  const [profile, setProfile] = useState(null);

  const isAdminRole = (auth.role || "").toLowerCase().includes("admin");
  const activeSub = profile?.activeSubscription;
  const isSubActive = activeSub && 
    (activeSub.status === "Active" || activeSub.status === "Approved") && 
    (new Date(activeSub.endDate) > new Date() || (typeof activeSub.daysLeft === "number" && activeSub.daysLeft > 0));

  const allowedFeatures = activeSub?.allowedFeatures || profile?.activeSubscription?.allowedFeatures || profile?.allowedFeatures;

  const isFeatureLocked = (featureKey, requireActiveSubOnly = false) => {
    if (isAdminRole) return false;
    if (!isSubActive) return true; // All pages locked if subscription / free trial has expired!
    if (requireActiveSubOnly) return false; // Active subscription grants access to core rank features!
    if (!featureKey) return false;
    if (allowedFeatures !== undefined && allowedFeatures !== null && Array.isArray(allowedFeatures)) {
      return !allowedFeatures.includes(featureKey);
    }
    return false;
  };

  const navGroups = useMemo(() => [
    {
      label: "Main",
      items: [
        { id: "dashboard",   path:'/user/dashboard', icon: FiGrid,     label: "Dashboard",    active: true },
        { id: "journey",  path:'/user/journey',    icon: FiActivity, label: "My Journey", isLocked: isFeatureLocked("journey_analytics") },
        { id: "trainingLibrary", path:'/user/traininglibrary', icon: GiLotus,   label: "Training Library", isLocked: isFeatureLocked(null, true) },
        { id: "liveSessions",  path:'/user/livesession',   icon: FiCalendar, label: "Live Sessions", isLocked: isFeatureLocked(null, true) },
        { id: "attendance",  path:'/user/attendance',   icon: FiActivity, label: "Attendance", isLocked: isFeatureLocked("attendance_logs") },
      ],
    },
    {
      label: "Wellness",
      items: [
        { id: "dietPlan", path:'/user/dietplan', icon: GiLotus,   label: "Diet Plans", oliveActive: true, isLocked: isFeatureLocked(null, true) },
        { id: "goals",  path:'/user/goals',  icon: FiTarget,  label: "Goals",    oliveActive: true, isLocked: isFeatureLocked("goals_tracker") },
      ],
    },
    {
      label: "Account",
      items: [
        { id: "achievements", path:'/user/achievements', icon: FiAward,    label: "Achievements", badge: isFeatureLocked("achievements") ? null : (stats?.streakDays ? Math.min(3, Math.floor(stats.streakDays / 7)) || null : null), isLocked: isFeatureLocked("achievements") },
        { id: "notification", path:'/user/notification', icon: FiMessageSquare,    label: "Notification", badge: unreadNotifCount || null },
        { id: "profile", path:'/user/profile',   icon: FiUser,     label: "Profile" },
        { id: "settings", path:'/user/settings', icon: FiSettings, label: "Settings" },
      ],
    },
  ], [stats, unreadNotifCount, videosCount, liveSessionsCount, allowedFeatures, auth, profile, isSubActive]);

  const fetchUserProfile = () => {
    const currentAuth = getAuth();
    setAuthState(currentAuth);
    if (currentAuth.token || currentAuth.user) {
      userAPI.getProfile().then(res => {
        if (res.data?.success && res.data.data) {
          setProfile(res.data.data);
          try {
            const updatedUser = { ...(currentAuth.user || {}), tier: res.data.data.tier, activeSubscription: res.data.data.activeSubscription };
            localStorage.setItem("divyayoga_user", JSON.stringify(updatedUser));
          } catch(e) {}
        }
      }).catch(e => console.warn(e));
    }
  };

  useEffect(() => {
    fetchUserProfile();

    const handleUpdate = () => fetchUserProfile();
    window.addEventListener("profile-updated", handleUpdate);
    window.addEventListener("storage", handleUpdate);
    return () => {
      window.removeEventListener("profile-updated", handleUpdate);
      window.removeEventListener("storage", handleUpdate);
    };
  }, []);

  const rawTier = activeSub?.packageName || profile?.tier || auth.user?.tier || auth.user?.subscriptionTier || "Free Trial";
  const lowTier = rawTier.toLowerCase().trim();

  let dynamicTierLabel = "Free Trial Yogi";
  if (lowTier.includes("ytt") || lowTier.includes("pro")) {
    dynamicTierLabel = "YTT Pro Yogi";
  } else if (lowTier.includes("premium")) {
    dynamicTierLabel = "Premium Yogi";
  } else if (lowTier.includes("standard")) {
    dynamicTierLabel = "Standard Yogi";
  } else if (lowTier.includes("basic")) {
    dynamicTierLabel = "Basic Yogi";
  } else if (rawTier && !lowTier.includes("free")) {
    dynamicTierLabel = `${rawTier} Yogi`;
  }

  const userSubLabel = isAdminRole ? "Administrator" : dynamicTierLabel;

  const handleLogout = () => {
    logout(); 
    navigate("/login", { replace: true });
  };
  const handleMouseEnter = () => {
    clearTimeout(hoverTimeout.current);
    if (!pinned) setExpanded(true);
  };

  const handleMouseLeave = () => {
    if (!pinned) {
      hoverTimeout.current = setTimeout(() => setExpanded(false), 220);
    }
  };

  const handleToggle = () => {
    const next = !pinned;
    setPinned(next);
    setExpanded(next);
  };

  return (
    <>
      {/* Left ambient glow (desktop only) */}
      <div
        className="hidden md:block fixed left-0 top-0 h-screen w-[500px] pointer-events-none z-0"
        style={{ background: "rgba(242,154,12,0.08)", filter: "blur(180px)" }}
      />

      {/* Top shimmer fade (desktop only) */}
      <div className="hidden md:block absolute top-0 left-0 right-0 h-[160px] pointer-events-none z-0"
        style={{ background: "linear-gradient(180deg,rgba(255,255,255,0.45),transparent)" }} />

      <motion.aside
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        animate={{ width: expanded ? 280 : 80 }}
        transition={{ duration: 0.42, ease: [0.25, 1, 0.5, 1] }}
        className="hidden md:flex fixed left-5 top-5 bottom-5 z-50 flex-col overflow-hidden"
        style={{
          background: "rgba(255,255,255,0.82)",
          backdropFilter: "blur(40px)",
          WebkitBackdropFilter: "blur(40px)",
          border: "1px solid rgba(255,255,255,0.95)",
          borderRadius: 30,
          boxShadow: "0 32px 80px rgba(0,0,0,0.09), inset 0 1px 0 rgba(255,255,255,1)",
        }}
      >
        {/* Ambient orbs */}
        <motion.div
          animate={{ y: [0, -18, 0], scale: [1, 1.05, 1] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-16 -left-16 w-48 h-48 rounded-full pointer-events-none"
          style={{ background: "rgba(242,154,12,0.12)", filter: "blur(50px)" }}
        />
        <motion.div
          animate={{ y: [0, 16, 0], scale: [1, 0.95, 1] }}
          transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -bottom-16 -right-16 w-40 h-40 rounded-full pointer-events-none"
          style={{ background: "rgba(107,138,90,0.1)", filter: "blur(40px)" }}
        />

        {/* Dot-grid watermark */}
        <div
          className="absolute inset-0 pointer-events-none rounded-[inherit] opacity-[0.035]"
          style={{ backgroundImage: "radial-gradient(circle,#000 1px,transparent 0)", backgroundSize: "20px 20px" }}
        />

        {/* Pin/Toggle button */}
        {expanded && (
        <motion.button
          onClick={handleToggle}
          whileHover={{ scale: 1.12 }}
          whileTap={{ scale: 0.9 }}
          className="absolute right-1.5 top-6 z-10 w-7 h-7 rounded-full flex items-center justify-center text-white text-[13px]"
          style={{
            background: "linear-gradient(135deg,#F29A0C,#E97818)",
            border: "2.5px solid rgba(255,248,238,0.9)",
            boxShadow: "0 4px 14px rgba(242,154,12,0.45)",
          }}
          title={pinned ? "Unpin sidebar" : "Pin sidebar open"}
        >
          {pinned ? <FiChevronLeft /> : <FiChevronRight />}
        </motion.button>
        )}

        {/* ── LOGO ── */}
        <div className="px-2 pt-5 pb-0 flex-shrink-0">
          <div className={`flex items-center gap-3 h-[58px] ${expanded ? 'justify-start px-1.5' : 'justify-center'}`}>
            <OrbitalLogo compact={!expanded} />
            <AnimatePresence>
              {expanded && (
                <motion.div
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -14 }}
                  transition={{ duration: 0.22, ease: [0.25, 1, 0.5, 1] }}
                  className="overflow-hidden whitespace-nowrap"
                >
                  <h2 className="text-[16px] font-black text-[#1F1F1F] leading-tight tracking-[0.8px]"
                    style={{ fontFamily: "'Poppins', sans-serif" }}>DIVYYOG</h2>
                  <p className="text-[10px] text-[#AAA] font-bold tracking-[1.5px] uppercase mt-0.5">Foundation</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Divider */}
        <div className="mx-3.5 my-3 h-px flex-shrink-0" style={{ background: "rgba(239,229,216,0.6)" }} />

        {/* ── NAV ── */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-2.5" style={{ scrollbarWidth: "none" }}>
          {navGroups.map((group, gi) => (
            <div key={group.label} className={gi > 0 ? "mt-3" : ""}>
              {/* Group label */}
              <AnimatePresence>
                {expanded && (
                  <motion.p
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.2 }}
                    className="text-[9px] font-black text-[#CCC] uppercase tracking-[1.8px] px-2.5 pt-2 pb-1.5"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {group.label}
                  </motion.p>
                )}
              </AnimatePresence>
              {/* Items */}
              <div className="flex flex-col gap-[3px]">
                {group.items.map(item => (
                  <NavItem
                    key={item.id}
                    item={item}
                    expanded={expanded}
                    isActive={location.pathname === item.path}
                    onClick={() => {
                      navigate(item.path);
                    }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div className="mx-3.5 my-2 h-px flex-shrink-0" style={{ background: "rgba(239,229,216,0.6)" }} />

        {/* ── FOOTER / USER ── */}
        <div className="px-2 pb-3 flex-shrink-0">
          <motion.div
            whileHover={{ y: -2 }}
            className={`rounded-[22px] cursor-pointer overflow-hidden transition-all duration-200 ${expanded ? 'p-3' : 'p-1.5 flex flex-col items-center'}`}
            style={{
              background: "rgba(255,248,238,0.85)",
              border: "1px solid rgba(242,154,12,0.14)",
              boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
              transition: "box-shadow 0.25s",
            }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = "0 8px 24px rgba(242,154,12,0.15)"}
            onMouseLeave={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)"}
          >
            <div className={`flex items-center gap-2.5 ${expanded ? 'justify-start w-full' : 'justify-center'}`}>
              {/* Avatar */}
              <div className="relative flex-shrink-0">
                <div
                  className={`${expanded ? 'w-[44px] h-[44px]' : 'w-[38px] h-[38px]'} rounded-[14px] flex items-center justify-center text-white text-[12px] font-black overflow-hidden`}
                  style={{
                    background: "linear-gradient(135deg,#F29A0C,#E97818)",
                    boxShadow: "0 6px 16px rgba(242,154,12,0.38)",
                    fontFamily: "'Poppins', sans-serif",
                    position: "relative",
                  }}
                >
                  {(() => {
                    const avatarUrl = profile?.profileImageUrl || profile?.avatarUrl || auth.user?.profileImageUrl || auth.user?.avatarUrl;
                    if (avatarUrl) {
                      return <img src={avatarUrl} alt="User Avatar" className="w-full h-full object-cover rounded-[14px]" />;
                    }
                    return (
                      <>
                        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg,rgba(255,255,255,0.2),transparent)" }} />
                        <span className="relative z-10">{getInitials(auth.user)}</span>
                      </>
                    );
                  })()}
                </div>
                {/* Online dot */}
                <div
                  className="absolute bottom-[0px] right-[0px] w-[9px] h-[9px] rounded-full border-2 z-20"
                  style={{ background: "#22C55E", borderColor: "rgba(255,248,238,0.95)" }}
                />
              </div>

              {/* User info */}
              <AnimatePresence>
                {expanded && (
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ duration: 0.2 }}
                    className="flex-1 min-w-0 overflow-hidden whitespace-nowrap"
                  >
                    <p className="text-[13px] font-bold text-[#1F1F1F] truncate" style={{ fontFamily: "'Inter', sans-serif" }}>{profile?.fullName || auth.user?.fullName}</p>
                    <p className="text-[11px] text-[#AAA] truncate">{userSubLabel}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Logout */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              className="mt-2.5 w-full h-[42px] rounded-[14px] flex items-center justify-center gap-2 text-[#E97818] text-[13px] font-semibold"
              style={{ background: "rgba(255,255,255,0.85)", border: "none", cursor: "pointer", fontFamily: "'Inter', sans-serif", transition: "background 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#fff"}
              onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.85)"}
              onClick={handleLogout}
            >
              <FiLogOut size={15} />
              <AnimatePresence>
                {expanded && (
                  <motion.span
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: "auto" }}
                    exit={{ opacity: 0, width: 0 }}
                    style={{ overflow: "hidden", whiteSpace: "nowrap" }}
                  >
                    Logout
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>
          </motion.div>

          {/* Version / Collapsed hint */}
          <AnimatePresence>
            {expanded ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center mt-2.5 flex items-center justify-center gap-1.5"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#F29A0C]" />
                <span className="text-[10.5px] font-semibold tracking-wider text-[#F29A0C]" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Divyyog v{packageJson.version}
                </span>
              </motion.div>
            ) : (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center text-[9px] font-bold uppercase tracking-[1px] mt-2 text-[#F29A0C]/80"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                v{packageJson.version}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
      </motion.aside>
    </>
  );
}